import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { Stack, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { memo, useDeferredValue, useMemo } from 'react';
import { type FieldPath, useFormContext, useWatch } from 'react-hook-form';
import { Tooltip } from 'src/components';
import { FormTextField } from 'src/components/Forms';
import { AccordionSummaryActionIconButton } from 'src/pages/Settings/memberships/components/plans-tab/accordion-summary-action-icon-button';
import {
  PlanInclusionChips,
  type PlanOption,
} from 'src/pages/Settings/memberships/components/plans-tab/plan-inclusion-chips';
import {
  PlanAccordionDetails,
  PlanAccordionSummary,
  PlanAccordionWrapper,
  PlanInactiveText,
  PlanName,
} from 'src/pages/Settings/memberships/components/plans-tab/styled';
import {
  type PlansTabFeatureValues,
  PlansTabField,
  type PlansTabFormValues,
} from 'src/pages/Settings/memberships/components/plans-tab/types';

const EMPTY_STRING_ARRAY: string[] = [];

const planOverridesWrapperSx = {
  backgroundColor: (theme: Theme) => theme.palette.custom.OV.sys.surfaceContainerLowest,
  border: (theme: Theme) => `${theme.typography.pxToRem(1)} solid ${theme.palette.divider}`,
  borderRadius: (theme: Theme) => theme.spacing(1),
  padding: 1.5,
} as const;

type FeatureCardProps = {
  index: number;
  featureId: string;
  enabledPlans: PlanOption[];
  expanded: boolean;
  onExpandedChange: (featureId: string, expanded: boolean) => void;
  onRemove: (index: number, featureId: string) => void;
  onDuplicate: (index: number) => void;
  onMove: (fromIndex: number, toIndex: number) => void;
  moveUpToIndex?: number;
  moveDownToIndex?: number;
  readOnly: boolean;
};

type FeatureFieldPath = FieldPath<PlansTabFormValues>;

const getFeatureFieldName = (
  index: number,
  field: keyof PlansTabFeatureValues,
  subPath?: string,
): FeatureFieldPath => {
  const basePath = `${PlansTabField.Features}.${index}.${field}`;
  return (subPath ? `${basePath}.${subPath}` : basePath) as FeatureFieldPath;
};

type FeatureOverridesProps = {
  index: number;
  plans: PlanOption[];
  defaultContentPlaceholder: string;
  defaultTooltipPlaceholder: string;
  disabled: boolean;
};

const FeatureOverrides = memo(function FeatureOverrides({
  index,
  plans,
  defaultContentPlaceholder,
  defaultTooltipPlaceholder,
  disabled,
}: FeatureOverridesProps) {
  if (!plans.length) {
    return null;
  }

  return (
    <Stack gap={2}>
      <Typography variant="body2" fontWeight={500}>
        Plan-specific overrides (optional)
      </Typography>
      <Stack gap={2}>
        {plans.map((plan) => {
          const overrideContentFieldName = getFeatureFieldName(
            index,
            'overrideContentByPlanId',
            plan.id,
          );
          const overrideTooltipFieldName = getFeatureFieldName(
            index,
            'overrideTooltipByPlanId',
            plan.id,
          );

          return (
            <Stack key={plan.id} gap={1.5} sx={planOverridesWrapperSx}>
              <Typography variant="caption" color="text.secondary">
                {plan.name}
              </Typography>
              <FormTextField
                type="textfield"
                name={overrideContentFieldName}
                label="Text"
                value=""
                disabled={disabled}
                textFieldProps={{
                  variant: 'outlined',
                  size: 'small',
                  fullWidth: true,
                  placeholder: defaultContentPlaceholder,
                  slotProps: {
                    input: {
                      sx: {
                        backgroundColor: (theme: Theme) =>
                          theme.palette.custom.OV.sys.surfaceContainerLowest,
                      },
                    },
                  },
                }}
              />
              <FormTextField
                type="textfield"
                name={overrideTooltipFieldName}
                label="Tooltip"
                value=""
                disabled={disabled}
                textFieldProps={{
                  variant: 'outlined',
                  size: 'small',
                  fullWidth: true,
                  multiline: true,
                  minRows: 2,
                  placeholder: defaultTooltipPlaceholder,
                  slotProps: {
                    input: {
                      sx: {
                        backgroundColor: (theme: Theme) =>
                          theme.palette.custom.OV.sys.surfaceContainerLowest,
                      },
                    },
                  },
                }}
              />
            </Stack>
          );
        })}
      </Stack>
    </Stack>
  );
});

export const FeatureCard = ({
  index,
  featureId,
  enabledPlans,
  expanded,
  onExpandedChange,
  onRemove,
  onDuplicate,
  onMove,
  moveUpToIndex,
  moveDownToIndex,
  readOnly,
}: FeatureCardProps) => {
  const { control, setValue } = useFormContext<PlansTabFormValues>();

  const featureValues = useWatch({
    control,
    name: `${PlansTabField.Features}.${index}`,
  }) as PlansTabFeatureValues | undefined;

  const name = featureValues?.name ?? '';
  const defaultContent = featureValues?.defaultContent ?? '';
  const defaultTooltip = featureValues?.defaultTooltip ?? '';
  const includedPlanIds = featureValues?.includedPlanIds ?? EMPTY_STRING_ARRAY;

  const featureTitle = name.trim() || defaultContent.trim() || 'Feature';
  const isIncludedInAnyPlan = includedPlanIds.length > 0;

  const includedEnabledPlans = useMemo(
    () => enabledPlans.filter((plan) => includedPlanIds.includes(plan.id)),
    [enabledPlans, includedPlanIds],
  );

  const deferredDefaultContentPlaceholder = useDeferredValue(defaultContent);
  const deferredDefaultTooltipPlaceholder = useDeferredValue(defaultTooltip);

  return (
    <PlanAccordionWrapper
      elevation={0}
      disableGutters
      expanded={expanded}
      onChange={(_, expandedValue) => {
        onExpandedChange(featureId, expandedValue);
      }}
      data-testid={`feature-card-${featureId}`}
    >
      <PlanAccordionSummary expandIcon={<ChevronRightIcon />}>
        <Tooltip title={featureTitle}>
          <PlanName variant="body2">{featureTitle}</PlanName>
        </Tooltip>
        {!isIncludedInAnyPlan ? (
          <PlanInactiveText variant="caption" whiteSpace="nowrap">
            (Not included)
          </PlanInactiveText>
        ) : null}
        {!readOnly ? (
          <Stack direction="row" alignItems="center" gap={0.5}>
            <AccordionSummaryActionIconButton
              aria-label="Move feature up"
              disabled={typeof moveUpToIndex !== 'number'}
              onAction={() => onMove(index, moveUpToIndex!)}
            >
              <ArrowUpwardIcon fontSize="inherit" />
            </AccordionSummaryActionIconButton>
            <AccordionSummaryActionIconButton
              aria-label="Move feature down"
              disabled={typeof moveDownToIndex !== 'number'}
              onAction={() => onMove(index, moveDownToIndex!)}
            >
              <ArrowDownwardIcon fontSize="inherit" />
            </AccordionSummaryActionIconButton>
            <AccordionSummaryActionIconButton
              aria-label="Duplicate feature"
              onAction={() => onDuplicate(index)}
            >
              <ContentCopyIcon fontSize="inherit" />
            </AccordionSummaryActionIconButton>
            <AccordionSummaryActionIconButton
              aria-label="Delete feature"
              onAction={() => onRemove(index, featureId)}
            >
              <DeleteOutlineIcon fontSize="inherit" />
            </AccordionSummaryActionIconButton>
          </Stack>
        ) : null}
      </PlanAccordionSummary>

      {expanded ? (
        <PlanAccordionDetails>
          <Stack gap={2} width="100%">
            <FormTextField
              type="textfield"
              name={getFeatureFieldName(index, 'name')}
              label="Name"
              required="Name is required"
              showErrorText
              disabled={readOnly}
              textFieldProps={{
                variant: 'outlined',
                size: 'small',
                fullWidth: true,
              }}
            />

            <FormTextField
              type="textfield"
              name={getFeatureFieldName(index, 'defaultContent')}
              label="Default text"
              required="Default text is required"
              showErrorText
              disabled={readOnly}
              textFieldProps={{
                variant: 'outlined',
                size: 'small',
                fullWidth: true,
                multiline: true,
                minRows: 2,
              }}
            />

            <FormTextField
              type="textfield"
              name={getFeatureFieldName(index, 'defaultTooltip')}
              label="Default tooltip (optional)"
              disabled={readOnly}
              textFieldProps={{
                variant: 'outlined',
                size: 'small',
                fullWidth: true,
                multiline: true,
                minRows: 2,
              }}
            />

            <Stack gap={1}>
              <Typography variant="body2" fontWeight={500}>
                Included in plans
              </Typography>
              <PlanInclusionChips
                plans={enabledPlans}
                selectedPlanIds={includedPlanIds}
                disabled={readOnly}
                onToggle={(planId) => {
                  const nextSelected = includedPlanIds.includes(planId)
                    ? includedPlanIds.filter((id) => id !== planId)
                    : [...includedPlanIds, planId];

                  const orderedSelected = enabledPlans
                    .map((plan) => plan.id)
                    .filter((id) => nextSelected.includes(id));

                  setValue(getFeatureFieldName(index, 'includedPlanIds'), orderedSelected, {
                    shouldDirty: true,
                    shouldTouch: true,
                  });
                }}
              />
            </Stack>

            <FeatureOverrides
              index={index}
              plans={includedEnabledPlans}
              defaultContentPlaceholder={deferredDefaultContentPlaceholder}
              defaultTooltipPlaceholder={deferredDefaultTooltipPlaceholder}
              disabled={readOnly}
            />
          </Stack>
        </PlanAccordionDetails>
      ) : null}
    </PlanAccordionWrapper>
  );
};

export const MemoFeatureCard = memo(FeatureCard);
